// @flow
import intlTelInput, { type Plugin } from 'intl-tel-input';
import 'intl-tel-input/build/css/intlTelInput.css';
import Cleave from 'cleave.js';

import retry from '@rover/utilities/retry';

/**
 * Most of this file was adapted from /frontend/app/js/components/intl_tel_input.js
 */

const DEFAULT_LOCALE = 'en-us';
const COUNTRIES_TO_USE_CLEAVE_FOR = ['us', 'ca'];
const DEFAULT_PREFERRED_COUNTRIES = ['us', 'ca', 'gb', 'de', 'es', 'fr', 'it', 'nl', 'no', 'se'];
const MISSING_MODULE_MESSAGE = 'Cannot find module';

const handleChange = (intlTelInputInstance: Plugin, onChange) => {
  onChange(intlTelInputInstance.getNumber(), intlTelInputInstance.isValidNumber());
};

const initCleave = inputRef =>
  new Cleave(inputRef, {
    blocks: [3, 3, 4],
    delimiter: '-',
    numericOnly: true,
  });

const getLocalizedCountries = async (locale: string) => {
  if (locale === DEFAULT_LOCALE) {
    return {};
  }

  return retry(async () => {
    try {
      // $FlowFixMe flow wants us to pass a string literal to import(), but this works fine?
      const localeModule = await import(`@rover/app/locale/${locale}/countries.json`);
      return localeModule.default;
    } catch (error) {
      // We expect this to fail if locale doesn't have countries defined
      // and we fall back to the US list. In all other cases, we should retry
      if (!error.message.includes(MISSING_MODULE_MESSAGE)) {
        throw error;
      }
    }
    return {};
  });
};

type CleanupFunction = () => void;
type PhonePluginRef = {
  cleanup: CleanupFunction,
  pluginInstance: Plugin,
};

export const initIntlPhoneInput = async (
  inputRef: HTMLInputElement,
  onChange: (string, boolean) => void,
  countryCode: string = 'us',
  locale: string = DEFAULT_LOCALE,
  preferredCountries: Array<string> = DEFAULT_PREFERRED_COUNTRIES
): Promise<PhonePluginRef> => {
  const localizedCountries = await getLocalizedCountries(locale);

  // extra insurance that we don't use an empty list of preferredCountries
  if (preferredCountries.length === 0) {
    preferredCountries = DEFAULT_PREFERRED_COUNTRIES; // eslint-disable-line no-param-reassign
  }

  const intlTelInputInstance: Plugin = intlTelInput(inputRef, {
    utilsScript: 'https://cdnjs.cloudflare.com/ajax/libs/intl-tel-input/16.0.4/js/utils.js',
    autoPlaceholder: 'off',
    preferredCountries,
    localizedCountries,
    initialCountry: countryCode,
  });

  let cleave;
  if (COUNTRIES_TO_USE_CLEAVE_FOR.includes(countryCode.toLowerCase())) {
    cleave = initCleave(inputRef);
  }

  inputRef.addEventListener('countrychange', () => {
    const newCountryCode = intlTelInputInstance.getSelectedCountryData().iso2;

    if (COUNTRIES_TO_USE_CLEAVE_FOR.includes(newCountryCode)) {
      cleave = initCleave(inputRef);
      handleChange(intlTelInputInstance, onChange);
    } else if (cleave) {
      cleave.destroy();
      handleChange(intlTelInputInstance, onChange);
    }
  });

  inputRef.addEventListener('input', () => {
    handleChange(intlTelInputInstance, onChange);
  });

  return {
    cleanup: () => {
      if (cleave) {
        cleave.destroy();
      }
      intlTelInputInstance.destroy();
    },
    pluginInstance: intlTelInputInstance,
  };
};

export default {
  initIntlPhoneInput,
};



// WEBPACK FOOTER //
// ../react-lib/src/components/formFields/IntlPhoneInput/IntlPhoneInputUtils.client.js